import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCardSx, tokens } from '../theme/tokens';
import { applyThemeSettings, DEFAULT_THEME_SETTINGS, normalizeThemeSettings, ThemeSettings } from '../theme/themeSettings';
import { themeSettingsService } from '../services/themeSettingsService';

const fieldHelper = 'Hex, rgba() oder CSS-Gradient';
const pickerHelper = 'Picker nur bei Hex-Farben aktiv.';

type ColorFieldDef = {
  key: keyof ThemeSettings['light'];
  label: string;
  helper?: string;
};

type StatusFieldDef = {
  key: keyof ThemeSettings['status'];
  label: string;
  helper?: string;
};

const PALETTE_FIELDS: ColorFieldDef[] = [
  { key: 'background', label: 'Background' },
  { key: 'cardBackground', label: 'Card Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'surfaceAlt', label: 'Surface Alt' },
  { key: 'text', label: 'Text' },
  { key: 'textSecondary', label: 'Text Secondary' },
  { key: 'border', label: 'Border' },
  { key: 'borderStrong', label: 'Border Strong' },
  { key: 'glowPrimary', label: 'Glow Primary' },
  { key: 'glowSecondary', label: 'Glow Secondary' },
  { key: 'shadow', label: 'Shadow' },
  { key: 'accent', label: 'Accent' },
  { key: 'accentSoft', label: 'Accent Soft' },
  { key: 'custom1', label: 'Custom 1' },
  { key: 'custom2', label: 'Custom 2' }
];

const STATUS_FIELDS: StatusFieldDef[] = [
  { key: 'success', label: 'Success' },
  { key: 'successSoft', label: 'Success Soft' },
  { key: 'successText', label: 'Success Text' },
  { key: 'warning', label: 'Warning' },
  { key: 'warningSoft', label: 'Warning Soft' },
  { key: 'warningText', label: 'Warning Text' },
  { key: 'error', label: 'Error' },
  { key: 'errorSoft', label: 'Error Soft' },
  { key: 'errorText', label: 'Error Text' },
  { key: 'info', label: 'Info' },
  { key: 'infoSoft', label: 'Info Soft' },
  { key: 'infoText', label: 'Info Text' }
];

const isHexColor = (value: string): string | null => {
  const trimmed = value.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
  if (short) {
    const [r, g, b] = short[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const full = /^#([0-9a-fA-F]{6})$/.exec(trimmed);
  if (full) return `#${full[1]}`.toLowerCase();
  return null;
};

const clamp = (value: number, min = 0, max = 255) => Math.min(max, Math.max(min, value));

const shiftHex = (hex: string, amount: number): string => {
  const normalized = hex.replace('#', '');
  const num = parseInt(normalized, 16);
  const r = clamp(((num >> 16) & 0xff) + amount);
  const g = clamp(((num >> 8) & 0xff) + amount);
  const b = clamp((num & 0xff) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const buildScale = (
  value: string,
  edgeStart: string,
  edgeEnd: string,
  neutralStart: string,
  neutralEnd: string
): string => {
  const hex = isHexColor(value);
  if (hex) {
    const light = shiftHex(hex, 48);
    const dark = shiftHex(hex, -48);
    return `linear-gradient(90deg, ${light}, ${hex}, ${dark})`;
  }
  const trimmed = value.trim();
  if (/gradient\(/i.test(trimmed)) return trimmed;
  if (trimmed) return `linear-gradient(90deg, ${edgeStart}, ${trimmed}, ${edgeEnd})`;
  return `linear-gradient(90deg, ${neutralStart}, ${neutralEnd})`;
};

type MessageState = {
  severity: 'success' | 'error' | 'info';
  text: string;
};

const AdminThemePage: React.FC = () => {
  const { token, canManageThemeSettings } = useAuth();
  const { darkMode } = useTheme();
  const textPrimary = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
  const textSecondary = darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary;
  const borderDefault = darkMode ? tokens.colors.dark.border : tokens.colors.light.border;
  const borderStrong = darkMode ? tokens.colors.dark.borderStrong : tokens.colors.light.borderStrong;
  const glowPrimary = darkMode ? tokens.colors.dark.glowPrimary : tokens.colors.light.glowPrimary;
  const glowSecondary = darkMode ? tokens.colors.dark.glowSecondary : tokens.colors.light.glowSecondary;
  const scaleEdgeStart = `color-mix(in srgb, ${textPrimary} 20%, transparent)`;
  const scaleEdgeEnd = `color-mix(in srgb, ${textPrimary} 45%, transparent)`;
  const scaleNeutralStart = darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt;
  const scaleNeutralEnd = darkMode ? tokens.colors.dark.surface : tokens.colors.light.surface;
  const [settings, setSettings] = useState<ThemeSettings>(DEFAULT_THEME_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<MessageState | null>(null);

  const normalizedSettings = useMemo(() => normalizeThemeSettings(settings), [settings]);

  const loadSettings = useCallback(async (force = false) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const loaded = await themeSettingsService.getThemeSettings(token, { force });
      setSettings(loaded);
      applyThemeSettings(loaded);
    } catch (e: any) {
      setError(e?.message || 'Theme-Einstellungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const saved = await themeSettingsService.updateThemeSettings(token, normalizedSettings);
      setSettings(saved);
      applyThemeSettings(saved);
      setMessage({ severity: 'success', text: 'Theme-Einstellungen gespeichert.' });
    } catch (e: any) {
      setError(e?.message || 'Theme-Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const updateRootField = (field: 'primary' | 'primaryHover', value: string) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const updatePaletteField = (mode: 'light' | 'dark', field: keyof ThemeSettings['light'], value: string) => {
    setSettings((prev) => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        [field]: value
      }
    }));
  };

  const updateStatusField = (field: keyof ThemeSettings['status'], value: string) => {
    setSettings((prev) => ({
      ...prev,
      status: {
        ...prev.status,
        [field]: value
      }
    }));
  };

  const renderColorField = (label: string, value: string, onChange: (next: string) => void) => {
    const hex = isHexColor(value);
    return (
      <Stack spacing={1} sx={{ flex: 1, minWidth: 260 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            helperText={fieldHelper}
            fullWidth
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: `1px solid ${borderStrong}`,
                background: value
              }}
            />
            <TextField
              type="color"
              value={hex || '#000000'}
              onChange={(e) => onChange(e.target.value)}
              helperText={pickerHelper}
              disabled={!hex}
              sx={{ width: 90 }}
            />
          </Stack>
        </Stack>
        <Box
          sx={{
            height: 8,
            borderRadius: 99,
            border: `1px solid ${borderDefault}`,
            background: buildScale(value, scaleEdgeStart, scaleEdgeEnd, scaleNeutralStart, scaleNeutralEnd)
          }}
        />
      </Stack>
    );
  };

  const handleReset = () => {
    setSettings(DEFAULT_THEME_SETTINGS);
    applyThemeSettings(DEFAULT_THEME_SETTINGS);
    setMessage({ severity: 'info', text: 'Standardwerte geladen. Bitte speichern, um sie global zu setzen.' });
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canManageThemeSettings) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Container>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell
      title="Admin: Design & Theme"
      actions={(
        <>
          <Button component={RouterLink} to="/admin" color="inherit">
            Zurueck
          </Button>
          <Button
            color="inherit"
            onClick={() => loadSettings(true)}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </>
      )}
      columns={{ xs: '1fr' }}
    >
      <Box
        sx={{
          background: darkMode
            ? `radial-gradient(circle at 10% 10%, ${glowSecondary}, transparent 55%), radial-gradient(circle at 90% 0%, ${glowPrimary}, transparent 50%), ${tokens.colors.dark.background}`
            : `radial-gradient(circle at 12% 10%, ${glowPrimary}, transparent 50%), radial-gradient(circle at 90% 0%, ${glowSecondary}, transparent 48%), ${tokens.colors.light.background}`,
          minHeight: '100%',
          width: '100%'
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 }, fontFamily: '"Space Grotesk", "IBM Plex Sans", "Segoe UI", sans-serif' }}>
          <Stack spacing={3}>
            <Paper sx={{ ...getCardSx(darkMode), p: { xs: 2.5, md: 4 } }} elevation={0}>
              <Stack spacing={2.5}>
                <Stack spacing={1}>
                  <Typography variant="overline" sx={{ letterSpacing: 2, color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }}>
                    Theme Studio
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: textPrimary }}>
                    Gestalte deine Markenwelt
                  </Typography>
                  <Typography variant="body1" sx={{ color: textSecondary, maxWidth: 720 }}>
                    Passe Farben fuer Hintergruende, Karten und Akzente an. Aenderungen werden direkt angewendet und koennen gespeichert oder zurueckgesetzt werden.
                  </Typography>
                </Stack>
                {(message || error || loading) && (
                  <Stack spacing={1.5}>
                    {message && (
                      <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>
                    )}
                    {error && (
                      <Alert severity="error">{error}</Alert>
                    )}
                    {loading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={28} />
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(0, 0.8fr)' } }}>
              <Stack spacing={2.5}>
                <Accordion defaultExpanded sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                      Globale Steuerung
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      {renderColorField('Primary', normalizedSettings.primary, (next) => updateRootField('primary', next))}
                      {renderColorField('Primary Hover', normalizedSettings.primaryHover, (next) => updateRootField('primaryHover', next))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                      Status Colors
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                      {STATUS_FIELDS.map((field) => (
                        <Box key={`status-${field.key}`} sx={{ flex: 1, minWidth: 260 }}>
                          {renderColorField(
                            field.label,
                            normalizedSettings.status[field.key],
                            (next) => updateStatusField(field.key, next)
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                      Light Mode
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                      {PALETTE_FIELDS.map((field) => (
                        <Box key={`light-${field.key}`} sx={{ flex: 1, minWidth: 280 }}>
                          {renderColorField(
                            field.label,
                            normalizedSettings.light[field.key],
                            (next) => updatePaletteField('light', field.key, next)
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                      Dark Mode
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                      {PALETTE_FIELDS.map((field) => (
                        <Box key={`dark-${field.key}`} sx={{ flex: 1, minWidth: 280 }}>
                          {renderColorField(
                            field.label,
                            normalizedSettings.dark[field.key],
                            (next) => updatePaletteField('dark', field.key, next)
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                      Aktionen
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={handleReset} disabled={saving}>
                        Standardwerte
                      </Button>
                      <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Speichern...' : 'Speichern'}
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>

              <Stack spacing={2.5}>
                <Paper sx={{ ...getCardSx(darkMode), p: { xs: 2, md: 3 } }} elevation={0}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                    Vorschau mit allen Farben
                  </Typography>
                </Paper>

                <Accordion defaultExpanded sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>Light Mode</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        borderRadius: 3,
                        p: 3,
                        background: normalizedSettings.light.background,
                        border: `1px solid ${normalizedSettings.light.border}`
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: normalizedSettings.light.surface,
                            border: `1px solid ${normalizedSettings.light.borderStrong}`
                          }}
                        >
                          <Typography variant="body2" sx={{ color: normalizedSettings.light.text, fontWeight: 600 }}>
                            Surface
                          </Typography>
                          <Typography variant="caption" sx={{ color: normalizedSettings.light.textSecondary }}>
                            {normalizedSettings.light.surface}
                          </Typography>
                        </Box>

                        <Paper
                          sx={{
                            p: 2,
                            background: normalizedSettings.light.cardBackground,
                            color: normalizedSettings.light.text,
                            boxShadow: normalizedSettings.light.shadow,
                            border: `1px solid ${normalizedSettings.light.border}`
                          }}
                          elevation={0}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: normalizedSettings.light.text }}>
                            Card Preview
                          </Typography>
                          <Typography variant="body2" sx={{ color: normalizedSettings.light.textSecondary, mb: 2 }}>
                            Sekundaerer Text und Kartenhintergrund.
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.light.accentSoft, border: `1px solid ${normalizedSettings.light.accent}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.light.accent, fontWeight: 600 }}>Accent</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.light.custom1, border: `1px solid ${normalizedSettings.light.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.light.text, fontWeight: 600 }}>Custom 1</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.light.custom2, border: `1px solid ${normalizedSettings.light.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.light.text, fontWeight: 600 }}>Custom 2</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.light.surfaceAlt, border: `1px solid ${normalizedSettings.light.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.light.text, fontWeight: 600 }}>Surface Alt</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Stack>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded sx={{ ...getCardSx(darkMode), p: 0, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>Dark Mode</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        borderRadius: 3,
                        p: 3,
                        background: normalizedSettings.dark.background,
                        border: `1px solid ${normalizedSettings.dark.border}`
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: normalizedSettings.dark.surface,
                            border: `1px solid ${normalizedSettings.dark.borderStrong}`
                          }}
                        >
                          <Typography variant="body2" sx={{ color: normalizedSettings.dark.text, fontWeight: 600 }}>
                            Surface
                          </Typography>
                          <Typography variant="caption" sx={{ color: normalizedSettings.dark.textSecondary }}>
                            {normalizedSettings.dark.surface}
                          </Typography>
                        </Box>

                        <Paper
                          sx={{
                            p: 2,
                            background: normalizedSettings.dark.cardBackground,
                            color: normalizedSettings.dark.text,
                            boxShadow: normalizedSettings.dark.shadow,
                            border: `1px solid ${normalizedSettings.dark.border}`
                          }}
                          elevation={0}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: normalizedSettings.dark.text }}>
                            Card Preview
                          </Typography>
                          <Typography variant="body2" sx={{ color: normalizedSettings.dark.textSecondary, mb: 2 }}>
                            Sekundaerer Text und Kartenhintergrund.
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.dark.accentSoft, border: `1px solid ${normalizedSettings.dark.accent}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.dark.accent, fontWeight: 600 }}>Accent</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.dark.custom1, border: `1px solid ${normalizedSettings.dark.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.dark.text, fontWeight: 600 }}>Custom 1</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.dark.custom2, border: `1px solid ${normalizedSettings.dark.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.dark.text, fontWeight: 600 }}>Custom 2</Typography>
                            </Box>
                            <Box sx={{ p: 1, borderRadius: 1, background: normalizedSettings.dark.surfaceAlt, border: `1px solid ${normalizedSettings.dark.border}` }}>
                              <Typography variant="caption" sx={{ color: normalizedSettings.dark.text, fontWeight: 600 }}>Surface Alt</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Stack>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>
    </UnitPageShell>
  );
};

export default AdminThemePage;
